import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, Modal, TextField, MenuItem, Alert} from '@mui/material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {adminApi} from '../../../../services'
import {parseError} from '../../../../utils/helpers'

export const PAYER_TYPES = [
    {value: 'dhs_mnits', label: 'Minnesota DHS (MN-ITS)'},
    {value: 'mco', label: 'Managed Care Organization'},
    {value: 'county', label: 'County'},
    {value: 'private', label: 'Private'},
]

export const SERVICE_CATEGORIES = [
    {value: 'supportive_services', label: 'Supportive Services'},
    {value: 'ics', label: 'ICS'},
    {value: 'all', label: 'All'},
]

export default ({open, setOpen, organization, payer, onSaved}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const editing = Boolean(payer?.id)

    const [payerName, setPayerName] = useState('')
    const [payerType, setPayerType] = useState('dhs_mnits')
    const [serviceCategory, setServiceCategory] = useState('all')
    const [providerNumber, setProviderNumber] = useState('')
    const [payerCode, setPayerCode] = useState('')
    const [problem, setProblem] = useState(null)

    useEffect(() => {
        if (!open) return
        setPayerName(payer?.payer_name || '')
        setPayerType(payer?.payer_type || 'dhs_mnits')
        setServiceCategory(payer?.service_category || 'all')
        setProviderNumber(payer?.provider_number || '')
        setPayerCode(payer?.hhax?.payer_code || '')
        setProblem(null)
    }, [open])

    const save = () => {
        if (!payerName.trim()) {
            setProblem('The payer name is required.')
            return
        }
        setProblem(null)
        setLoading(true)

        const details = {
            payer_name: payerName.trim(),
            payer_type: payerType,
            service_category: serviceCategory,
            provider_number: providerNumber.trim() || null,
        }
        // The EVV code is a platform-admin write on its own block, and creating
        // a payer does not accept it — so a new payer is created first and the
        // code applied to it. Editing sends both together, which the API takes
        // in one request.
        const request = editing
            ? adminApi.updatePayer(organization?.id, payer.id, {...details, hhax: {payer_code: payerCode.trim().toUpperCase()}})
            : adminApi
                  .createPayer(organization?.id, details)
                  .then(({data}) =>
                      payerCode.trim()
                          ? adminApi.updatePayer(organization?.id, data?.data?.id, {hhax: {payer_code: payerCode.trim().toUpperCase()}})
                          : data,
                  )

        request
            .then(() => {
                setLoading(false)
                setOpen(false)
                setMessageBox({
                    status: 'success',
                    title: editing ? 'Payor updated' : 'Payor added',
                    description: `${payerName.trim()} is available to this organization's branches.`,
                })
                onSaved?.()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                // A rejected EVV code or a duplicate name is fixable here, so the
                // dialog keeps what was typed rather than closing on it.
                if (error?.response?.status === 400 || error?.response?.status === 409) {
                    setProblem(description || title)
                    return
                }
                setOpen(false)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not save payor',
                    description: description || 'There was an error saving this payor. Please try again later.',
                })
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            {editing ? `Edit ${payer?.payer_name}` : 'Add Payor'}
                        </Typography>
                        <br />
                        <Typography variant="body2" color="text.secondary">
                            Payors belong to {organization?.name}. Every active branch can bill them.
                        </Typography>
                    </div>
                    <br />
                    {problem && (
                        <>
                            <Alert severity="error">{problem}</Alert>
                            <br />
                        </>
                    )}
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Payor Name
                        </Typography>
                        <TextField placeholder="Minnesota DHS" fullWidth value={payerName} onChange={e => setPayerName(e.target.value)} />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Payor Type
                        </Typography>
                        <TextField select fullWidth value={payerType} onChange={e => setPayerType(e.target.value)}>
                            {PAYER_TYPES.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Service Category
                        </Typography>
                        <TextField select fullWidth value={serviceCategory} onChange={e => setServiceCategory(e.target.value)}>
                            {SERVICE_CATEGORIES.map(category => (
                                <MenuItem key={category.value} value={category.value}>
                                    {category.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Provider Number
                        </Typography>
                        <TextField
                            placeholder="The agency's provider number with this payor"
                            fullWidth
                            value={providerNumber}
                            onChange={e => setProviderNumber(e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            EVV Payor Code
                        </Typography>
                        <TextField placeholder="MNCS" fullWidth value={payerCode} onChange={e => setPayerCode(e.target.value.toUpperCase())} />
                        <Typography variant="body2" color="text.secondary">
                            Sent to HHAeXchange on every visit for this payor. Without it, its visits cannot be reported.
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" fullWidth onClick={save}>
                            {editing ? 'Save Changes' : 'Add Payor'}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
